import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';

class FileProcessingService {
  constructor() {
    this.initializePdfJs();
  }

  initializePdfJs() {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
  }

  async processFile(file) {
    try {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('File size should be less than 10MB');
      }

      console.log('Processing file:', file.name, 'Type:', file.type);

      if (file.name.toLowerCase().endsWith('.ppt') || file.name.toLowerCase().endsWith('.pptx') ||
          file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
          file.type === 'application/vnd.ms-powerpoint') {
        return await this.processPPT(file);
      }

      switch (file.type) {
        case 'application/pdf':
          return await this.processPDF(file);
        
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'application/msword':
          return await this.processWord(file);
        
        case 'text/plain':
          return await this.processText(file);
        
        case 'text/markdown':
        case 'text/md':
          return await this.processMarkdown(file);
        
        default:
          throw new Error('Unsupported file type: ' + file.type);
      }
    } catch (error) {
      console.error('File processing error:', error);
      throw error;
    }
  }

  async processPDF(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let text = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        text += content.items.map(item => item.str).join(' ') + '\n';
      }
      
      return this.cleanText(text);
    } catch (error) {
      console.error('PDF processing error:', error);
      throw new Error('Failed to process PDF file');
    }
  }

  async processWord(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return this.cleanText(result.value);
    } catch (error) {
      console.error('Word processing error:', error);
      throw new Error('Failed to process Word file');
    }
  }

  async processText(file) {
    try {
      const text = await file.text();
      return this.cleanText(text);
    } catch (error) {
      console.error('Text processing error:', error);
      throw new Error('Failed to process text file');
    }
  }

  async processMarkdown(file) {
    try {
      const text = await file.text();
      // Remove markdown syntax
      return this.cleanText(text.replace(/[#*_~`]/g, ''));
    } catch (error) {
      console.error('Markdown processing error:', error);
      throw new Error('Failed to process Markdown file');
    }
  }

  async processPPT(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'ppt');
      formData.append('filename', file.name);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ai/summarize-ppt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to summarize PowerPoint');
      }

      const { summary } = await response.json();
      return summary;
    } catch (error) {
      console.error('PPT processing error:', error);
      throw new Error('Failed to process PPT file');
    }
  }

  cleanText(text) {
    return text
      .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
      .replace(/[\r\n]+/g, '\n')  // Normalize line breaks
      .replace(/[^\x20-\x7E\n]/g, '') // Remove non-printable characters
      .trim();  // Remove leading/trailing whitespace
  }
}

export const fileProcessingService = new FileProcessingService(); 